import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import {
  ICrmBrandContactDetailsData,
  useGetContactDetailsByIdApi,
  useUpdateCrmBrandContactDetailsApi,
} from 'apis/crm-brand-contact-details';
import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface EditContactDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactDetails: ICrmBrandContactDetailsData;
}

interface FormData {
  brand: { value: number; label: string } | null;
  contactName: string;
  areaName: string;
  designation: string;
  phoneNumber: string;
  email: string;
}

export const EditContactDetailsModal: React.FC<EditContactDetailsModalProps> = ({
  isOpen,
  onClose,
  contactDetails,
}) => {
  // Fetch complete contact details including brand info
  const { data: fullContactDetails, isLoading: isLoadingDetails } = useGetContactDetailsByIdApi(contactDetails.id, {
    enabled: isOpen && !!contactDetails.id,
  });

  // Self-contained mutation with success handling
  const { mutate: updateContactDetails, isLoading: isUpdating } = useUpdateCrmBrandContactDetailsApi({
    onSuccess: () => {
      onCloseModal();
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  // Async load brands with search functionality
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  // Reset form when modal opens or contact details are loaded
  useEffect(() => {
    if (isOpen && fullContactDetails) {
      const brandOption = fullContactDetails.brand
        ? {
            value: fullContactDetails.brand.id,
            label: fullContactDetails.brand.name,
          }
        : null;

      reset({
        brand: brandOption,
        contactName: fullContactDetails.contactName || '',
        designation: fullContactDetails.designation || '',
        phoneNumber: fullContactDetails.phoneNumber || '',
        email: fullContactDetails.email || '',
      });
    }
  }, [isOpen, fullContactDetails, reset]);

  // Memoized close handler
  const onCloseModal = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // Memoized submit handler
  const onSubmit = useCallback(
    (data: FormData) => {
      const payload = {
        contactName: data.contactName.trim(),
        designation: data.designation.trim() || undefined,
        phoneNumber: data.phoneNumber.trim() || undefined,
        email: data.email.trim() || undefined,
      };

      updateContactDetails({
        id: contactDetails.id,
        body: payload,
      });
    },
    [updateContactDetails, contactDetails.id]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Contact Details</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} as='form' onSubmit={handleSubmit(onSubmit)}>
            <FormControl isRequired isInvalid={!!errors.brand}>
              <FormLabel>Brand</FormLabel>
              <Controller
                name='brand'
                control={control}
                rules={{ required: 'Brand is required' }}
                render={({ field }) => (
                  <ChakraAsyncSelect
                    {...field}
                    loadOptions={loadBrandOptions}
                    placeholder='Search and select a brand...'
                    isClearable
                    defaultOptions
                    cacheOptions
                    isDisabled={true} // Brand cannot be changed in edit mode
                    isLoading={isLoadingDetails}
                    loadingMessage={() => 'Loading current brand...'}
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `No brands found for "${inputValue}"` : 'Type to search brands'
                    }
                  />
                )}
              />
              <FormErrorMessage>{errors.brand?.message}</FormErrorMessage>
              {isLoadingDetails && (
                <FormLabel fontSize='xs' color='gray.500' mt={1}>
                  Loading current brand information...
                </FormLabel>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.contactName}>
              <FormLabel>Contact Name</FormLabel>
              <Input
                {...register('contactName', {
                  required: 'Contact name is required',
                  minLength: { value: 2, message: 'Contact name must be at least 2 characters' },
                })}
                placeholder='Enter contact name'
                isDisabled={isLoadingDetails}
              />
              <FormErrorMessage>{errors.contactName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.designation}>
              <FormLabel>Designation</FormLabel>
              <Input
                {...register('designation', {
                  minLength: { value: 2, message: 'Designation must be at least 2 characters' },
                })}
                placeholder='Enter designation (optional)'
                isDisabled={isLoadingDetails}
              />
              <FormErrorMessage>{errors.designation?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.phoneNumber}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                {...register('phoneNumber', {
                  pattern: {
                    value: /^[\d\s\-+()]+$/,
                    message: 'Please enter a valid phone number',
                  },
                })}
                placeholder='+971501234567'
                isDisabled={isLoadingDetails}
              />
              <FormErrorMessage>{errors.phoneNumber?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel>Email</FormLabel>
              <Input
                type='email'
                placeholder='contact@brand.com'
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
                isDisabled={isLoadingDetails}
              />
              <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing='md'>
            <Button variant='ghost' colorScheme='gray' onClick={onCloseModal}>
              Cancel
            </Button>
            <Button
              variant='solid'
              colorScheme='blue'
              onClick={handleSubmit(onSubmit)}
              isLoading={isUpdating || isLoadingDetails}
              loadingText={isLoadingDetails ? 'Loading...' : 'Updating...'}
              isDisabled={isLoadingDetails}>
              Update Contact Details
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
